import { EventSource, metricsBus } from "@/common/buses";
import { useCallback, useEffect, useState } from "react";

import { apiMain } from "@/common/services";
import { showNotification } from "@/components/alerts/Notifications";
import { useUserStore } from "@/common/store/userStore";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";
import { useAnalyticsStore } from "@/common/store/analyticsStore";

export const useFollowing = () => {
  const currentUser = useUserStore((state) => state.currentUser?.username);
  const [following, setFollowing] = useState<string[]>([]);
  const { navigateSignIn } = useAuthNavigate();
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const sendFollow = (id: string) => {
    showNotification({
      title: `Started following ${id}`,
      variant: "success"
    });

    apiMain.followUser(id, eventSource?.id ?? null).then(() => {
      console.debug(`Successfully subscribed to ${id}.`);
    }).then(() => {
      metricsBus.emit("followUserClicked", {
        userName: id,
        source: eventSource?.name ?? EventSource.Other,
        sourceId: eventSource?.id ?? null
      });
    }).catch((e: Error) => {
      showNotification({
        title: `Error subscribing to ${id}: ${e.message}.`,
        variant: "error"
      });
      console.error(`Error subscribing to ${id}: ${e.message}.`);
    });
  };

  const sendUnfollow = (id: string) => {
    showNotification({
      title: `Stopped following ${id}`,
      variant: "success"
    });

    apiMain.unfollowUser(id).then(() => {
      console.debug(`Successfully unsubscribed from ${id}.`);
    }).then(() => {
      metricsBus.emit("unfollowUserClicked", {
        userName: id,
        source: eventSource?.name ?? EventSource.Other,
        sourceId: eventSource?.id ?? null
      });
    }).catch((e: Error) => {
      showNotification({
        title: `Error unsubscribing from ${id}: ${e.message}.`,
        variant: "success"
      });
      console.error(`Error unsubscribing from ${id}: ${e.message}.`);
    });
  };

  const follow = useCallback((followId: string) => {
    setFollowing(prev => {
      sendFollow(followId);
      const without = prev.filter((userName: string): boolean => userName !== followId);
      return [...without, followId];
    });
  }, []);

  const unfollow = useCallback((unfollowId: string) => {
    sendUnfollow(unfollowId);
    setFollowing(prev => prev.filter((userName: string): boolean => userName !== unfollowId));
  }, []);

  const toggleSubscription = useCallback((userName: string) => {
    if (!currentUser) {
      metricsBus.emit("followUserClickedAnonymously", {
        userName,
        source: eventSource?.name ?? EventSource.Other,
        sourceId: eventSource?.id ?? null
      });

      navigateSignIn();
      return;
    }

    const isFollowing = following.indexOf(userName) >= 0;

    setFollowing((prev: string[]) => {
      if (isFollowing) {
        return prev.filter((id: string) => id !== userName);
      } else {
        return [...prev, userName];
      }
    });

    if (isFollowing) {
      sendUnfollow(userName);
    } else {
      sendFollow(userName);
    }
  }, [following]);

  useEffect(() => {
    apiMain.getFollowingIds().then((ids: string[]) => {
      setFollowing(ids);
      console.debug(`Subscription list read, ${ids.length} items.`);
    }).catch((e: Error) => {
      console.error(`ERROR reading subscription list: ${e.message}.`);
    });
  }, []);

  return {
    following,
    follow,
    unfollow,
    toggleSubscription,
  };
};
